import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  Alert,
  Chip,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  LocalOffer as DiscountIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import BackButton from '../../components/common/BackButton';
import { firebaseDiscountService } from '../../services/firebaseDiscountService';

const AdminDiscounts = () => {
  const [discountCodes, setDiscountCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    type: 'percentage',
    value: '',
    minOrderValue: '',
    maxDiscount: '',
    maxUses: '',
    expiresAt: '',
    active: true
  });

  // Cargar códigos de descuento
  const loadDiscountCodes = async () => {
    try {
      setLoading(true);
      const codes = await firebaseDiscountService.getAll();
      setDiscountCodes(codes);
    } catch (error) {
      console.error('Error al cargar códigos:', error);
      toast.error('Error al cargar los códigos de descuento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDiscountCodes();
  }, []);

  // Abrir diálogo para crear/editar
  const openDialog = (discount = null) => {
    if (discount) {
      setEditingDiscount(discount);
      setFormData({
        code: discount.code || '',
        description: discount.description || '',
        type: discount.type || 'percentage',
        value: discount.value || '',
        minOrderValue: discount.minOrderValue || '',
        maxDiscount: discount.maxDiscount || '',
        maxUses: discount.maxUses || '',
        expiresAt: discount.expiresAt ? 
          new Date(discount.expiresAt).toISOString().split('T')[0] : '',
        active: discount.active !== false
      });
    } else {
      setEditingDiscount(null);
      setFormData({
        code: '',
        description: '',
        type: 'percentage',
        value: '',
        minOrderValue: '',
        maxDiscount: '',
        maxUses: '',
        expiresAt: '',
        active: true
      });
    }
    setDialogOpen(true);
  };

  // Guardar código de descuento
  const handleSave = async () => {
    try {
      // Validaciones
      if (!formData.code.trim()) {
        toast.error('El código es obligatorio');
        return;
      }
      if (!formData.description.trim()) {
        toast.error('La descripción es obligatoria');
        return;
      }
      if (!formData.value || isNaN(formData.value) || formData.value <= 0) {
        toast.error('El valor debe ser un número válido mayor que 0');
        return;
      }

      const discountData = {
        ...formData,
        code: formData.code.toUpperCase().trim(),
        value: parseFloat(formData.value),
        minOrderValue: parseFloat(formData.minOrderValue) || null,
        maxDiscount: parseFloat(formData.maxDiscount) || null,
        maxUses: parseInt(formData.maxUses) || null,
        expiresAt: formData.expiresAt ? new Date(formData.expiresAt) : null
      };

      if (editingDiscount) {
        discountData.id = editingDiscount.id;
      }

      await firebaseDiscountService.createOrUpdate(discountData);
      
      toast.success(editingDiscount ? 'Código actualizado' : 'Código creado');
      setDialogOpen(false);
      loadDiscountCodes();
    } catch (error) {
      console.error('Error al guardar código:', error);
      toast.error('Error al guardar el código de descuento');
    }
  };

  // Copiar código al portapapeles
  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Código copiado al portapapeles');
  };

  // Eliminar código de descuento
  const handleDelete = async (discount) => {
    const confirmMessage = `¿Estás seguro de eliminar el código "${discount.code}"?

Detalles del código:
- Descripción: ${discount.description}
- Tipo: ${discount.type === 'percentage' ? 'Porcentaje' : 'Cantidad fija'}
- Valor: ${discount.type === 'percentage' ? discount.value + '%' : '€' + discount.value}
- Usos actuales: ${discount.usedCount || 0}${discount.maxUses ? ' / ' + discount.maxUses : ''}

⚠️ Esta acción no se puede deshacer.`;

    if (window.confirm(confirmMessage)) {
      try {
        await firebaseDiscountService.delete(discount.id);
        toast.success('Código de descuento eliminado correctamente');
        loadDiscountCodes();
      } catch (error) {
        console.error('Error al eliminar código:', error);
        toast.error('Error al eliminar el código de descuento');
      }
    }
  };

  // Alternar estado activo
  const toggleActive = async (discount) => {
    try {
      const updatedDiscount = { ...discount, active: !discount.active };
      await firebaseDiscountService.createOrUpdate(updatedDiscount);
      toast.success(`Código ${updatedDiscount.active ? 'activado' : 'desactivado'}`);
      loadDiscountCodes();
    } catch (error) {
      console.error('Error al cambiar estado:', error);
      toast.error('Error al cambiar el estado');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <BackButton to="/admin" />
      
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            mb: 2,
            fontFamily: '"Playfair Display", serif',
            fontWeight: 600,
            color: 'secondary.main'
          }}
        >
          <DiscountIcon sx={{ fontSize: 40 }} />
          Códigos de Descuento
        </Typography>
      </Box>

      {/* Botón Crear */}
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => openDialog()}
        sx={{ mb: 3 }}
      >
        Nuevo Código de Descuento
      </Button>

      {/* Tabla de códigos */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Código</strong></TableCell>
              <TableCell><strong>Descripción</strong></TableCell>
              <TableCell><strong>Tipo/Valor</strong></TableCell>
              <TableCell><strong>Restricciones</strong></TableCell>
              <TableCell><strong>Usos</strong></TableCell>
              <TableCell><strong>Estado</strong></TableCell>
              <TableCell><strong>Acciones</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Cargando códigos...
                </TableCell>
              </TableRow>
            ) : discountCodes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No hay códigos configurados
                </TableCell>
              </TableRow>
            ) : (
              discountCodes.map((discount) => (
                <TableRow key={discount.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                        {discount.code}
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={() => copyToClipboard(discount.code)}
                        title="Copiar código"
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>{discount.description}</TableCell>
                  <TableCell>
                    {discount.type === 'percentage' ? (
                      <Chip 
                        label={`${discount.value}%`} 
                        color="primary" 
                        size="small"
                      />
                    ) : (
                      <Chip 
                        label={`€${discount.value}`} 
                        color="secondary" 
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box>
                      {discount.minOrderValue && (
                        <Typography variant="caption" display="block">
                          Min: €{discount.minOrderValue}
                        </Typography>
                      )}
                      {discount.maxDiscount && (
                        <Typography variant="caption" display="block">
                          Máx desc: €{discount.maxDiscount}
                        </Typography>
                      )}
                      {discount.expiresAt && (
                        <Box>
                          <Typography 
                            variant="caption" 
                            display="block" 
                            color={
                              new Date(discount.expiresAt) < new Date() ? 'error.main' : 'text.secondary'
                            }
                          >
                            Expira: {new Date(discount.expiresAt).toLocaleDateString('es-ES')}
                          </Typography>
                          {new Date(discount.expiresAt) < new Date() && (
                            <Chip label="Expirado" color="error" size="small" sx={{ mt: 0.5 }} />
                          )}
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {discount.usedCount || 0}
                      {discount.maxUses ? ` / ${discount.maxUses}` : ' / ∞'}
                    </Typography>
                    {discount.maxUses && (
                      <Box>
                        <Typography 
                          variant="caption" 
                          color={
                            ((discount.usedCount || 0) / discount.maxUses) > 0.9 ? 'error.main' :
                            ((discount.usedCount || 0) / discount.maxUses) > 0.7 ? 'warning.main' :
                            'text.secondary'
                          }
                        >
                          {Math.round(((discount.usedCount || 0) / discount.maxUses) * 100)}% usado
                        </Typography>
                        {((discount.usedCount || 0) >= discount.maxUses) && (
                          <Chip label="Agotado" color="error" size="small" sx={{ mt: 0.5 }} />
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={discount.active}
                            onChange={() => toggleActive(discount)}
                            size="small"
                            color="success"
                          />
                        }
                        label={discount.active ? 'Activo' : 'Inactivo'}
                        sx={{ m: 0 }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <IconButton 
                      onClick={() => openDialog(discount)}
                      color="primary"
                      size="small"
                      title="Editar código"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      onClick={() => handleDelete(discount)}
                      color="error"
                      size="small"
                      title="Eliminar código"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Diálogo de crear/editar */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingDiscount ? 'Editar Código de Descuento' : 'Nuevo Código de Descuento'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Código de Descuento"
                  value={formData.code}
                  onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  placeholder="DESCUENTO10"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descripción"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descuento del 10% en toda la tienda"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipo de Descuento</InputLabel>
                  <Select
                    value={formData.type}
                    onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                    label="Tipo de Descuento"
                  >
                    <MenuItem value="percentage">Porcentaje (%)</MenuItem>
                    <MenuItem value="fixed">Cantidad Fija (€)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={formData.type === 'percentage' ? 'Porcentaje (%)' : 'Cantidad (€)'}
                  type="number"
                  value={formData.value}
                  onChange={(e) => setFormData(prev => ({ ...prev, value: e.target.value }))}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Pedido Mínimo (€)"
                  type="number"
                  value={formData.minOrderValue}
                  onChange={(e) => setFormData(prev => ({ ...prev, minOrderValue: e.target.value }))}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Máximo de Usos"
                  type="number"
                  value={formData.maxUses}
                  onChange={(e) => setFormData(prev => ({ ...prev, maxUses: e.target.value }))}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.active}
                      onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                    />
                  }
                  label="Código Activo"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} startIcon={<CancelIcon />}>
            Cancelar
          </Button>
          <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />}>
            {editingDiscount ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Información adicional */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Gestión de códigos:</strong>
          <br />• <strong>Activar/Desactivar:</strong> Usa el switch en la columna Estado para habilitar o deshabilitar códigos
          <br />• <strong>Eliminar:</strong> El botón rojo elimina permanentemente el código (no se puede deshacer)
          <br />• <strong>Copiar:</strong> Haz clic en el icono junto al código para copiarlo al portapapeles
          <br />• <strong>Restricciones:</strong> Se muestran límites de pedido mínimo, descuento máximo y fecha de expiración
          <br />• <strong>Usos:</strong> Seguimiento en tiempo real de cuántas veces se ha usado cada código
        </Typography>
      </Alert>
    </Container>
  );
};

export default AdminDiscounts; 